import * as z from "zod";
import {
  type AccountId,
  accountIdFormat,
  shipmentLegIdFormat,
  shipmentLegParcelIdFormat,
} from "../ids";
import { TrackingDetailSchema } from "./outbound-shipment-label";

/**
 * The data model is leg-generic: a `shipment_leg` is a consolidation leg that
 * wraps N inner parcels. v2 builds `first_mile` (merchant -> APC/EPG
 * consolidator); `last_mile` is reserved so a future flow is not a migration.
 */
export const SHIPMENT_LEG_KINDS = ["first_mile", "last_mile"] as const;
export type ShipmentLegKind = (typeof SHIPMENT_LEG_KINDS)[number];

/** Operational lifecycle, driven by Redo RPCs. */
export const SHIPMENT_LEG_STATUSES = ["label_purchased", "voided"] as const;
export type ShipmentLegStatus = (typeof SHIPMENT_LEG_STATUSES)[number];

/** Transit state, driven by EasyPost ingestion; independent of `status`. */
export const SHIPMENT_LEG_TRACKING_STATUSES = [
  "in_transit",
  "exception",
  "delivered",
] as const;
export type ShipmentLegTrackingStatus =
  (typeof SHIPMENT_LEG_TRACKING_STATUSES)[number];

const ShipmentLegServiceSchema = z.enum(["apc", "epg"]);
const ShipmentLegCarrierSchema = z.enum(["fedex", "ups", "dhl"]);

/**
 * A tracking event as persisted on `shipment_leg.tracking_events`. Same shape
 * as a fulfillment group's `trackingDetails` so the merged timeline is one
 * event type. `datetime` is coerced because JSONB round-trips it as a string;
 * `status` is widened to a plain string so an unexpected carrier status never
 * fails a leg read (it is display data, not control flow).
 */
const StoredTrackingEventSchema_ = TrackingDetailSchema.extend({
  datetime: z.coerce.date(),
  status: z.string(),
});
export const StoredTrackingEventSchema: z.ZodType<StoredTrackingEvent> =
  StoredTrackingEventSchema_;
export interface StoredTrackingEvent
  extends z.infer<typeof StoredTrackingEventSchema_> {}

/**
 * Collapses an EasyPost tracker status into the coarse transit state stored on
 * `shipment_leg.tracking_status`. Terminal failure-like statuses become
 * `exception`; `delivered` passes through; everything else is `in_transit`.
 */
export function mapEasyPostStatusToLegTrackingStatus(
  status: string,
): ShipmentLegTrackingStatus {
  switch (status) {
    case "delivered":
      return "delivered";
    case "return_to_sender":
    case "failure":
    case "error":
    case "cancelled":
    case "expired":
      return "exception";
    default:
      return "in_transit";
  }
}

const ShipmentLegRowSchema_ = z.object({
  id: shipmentLegIdFormat.schema,
  accountId: accountIdFormat.schema,
  teamId: z.string(),
  kind: z.enum(SHIPMENT_LEG_KINDS),
  service: ShipmentLegServiceSchema,
  fulfillmentCenter: z.string(),
  carrier: ShipmentLegCarrierSchema,
  easyPostShipmentId: z.string(),
  trackingNumber: z.string(),
  labelUrl: z.string(),
  cost: z.string().nullable(),
  currency: z.string(),
  purchasedAt: z.date(),
  easyPostTrackerId: z.string(),
  trackingEvents: z.array(StoredTrackingEventSchema_),
  lastTrackedAt: z.date().nullable(),
  weight: z.number(),
  length: z.number(),
  width: z.number(),
  height: z.number(),
  dimensionUnit: z.string(),
  weightUnit: z.string(),
  status: z.enum(SHIPMENT_LEG_STATUSES),
  trackingStatus: z.enum(SHIPMENT_LEG_TRACKING_STATUSES).nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export const ShipmentLegRowSchema: z.ZodType<ShipmentLegRow> =
  ShipmentLegRowSchema_;
export interface ShipmentLegRow extends z.infer<typeof ShipmentLegRowSchema_> {}

const ShipmentLegParcelRowSchema_ = z.object({
  id: shipmentLegParcelIdFormat.schema,
  shipmentLegId: shipmentLegIdFormat.schema,
  fulfillmentGroupId: z.string(),
  trackerId: z.string(),
  trackingCode: z.string(),
  addedAt: z.date(),
});
export const ShipmentLegParcelRowSchema: z.ZodType<ShipmentLegParcelRow> =
  ShipmentLegParcelRowSchema_;
export interface ShipmentLegParcelRow
  extends z.infer<typeof ShipmentLegParcelRowSchema_> {}

/**
 * One scanned inner parcel destined for an overbox. Held client-side while the
 * box is open; sealed into `shipment_leg_parcel` rows at print time.
 */
const OverboxScannedParcelSchema_ = z.object({
  /** Mongo fulfillment group `_id`. */
  fulfillmentGroupId: z.string(),
  /** EasyPost tracker id of the specific inner parcel. */
  trackerId: z.string(),
  /** Scanned barcode that resolved to this parcel. */
  trackingCode: z.string(),
});
export const OverboxScannedParcelSchema: z.ZodType<OverboxScannedParcel> =
  OverboxScannedParcelSchema_;
export interface OverboxScannedParcel
  extends z.infer<typeof OverboxScannedParcelSchema_> {}

export interface CreateShipmentLegInput {
  accountId: AccountId;
  teamId: string;
  service: "apc" | "epg";
  fulfillmentCenter: string;
  carrier: "fedex" | "ups" | "dhl";
  easyPostShipmentId: string;
  trackingNumber: string;
  labelUrl: string;
  cost: string | null;
  currency: string;
  easyPostTrackerId: string;
  weight: number;
  length: number;
  width: number;
  height: number;
  dimensionUnit: string;
  weightUnit: string;
  contents: OverboxScannedParcel[];
}

/**
 * A leg may be voided only while it is still `label_purchased` and no transit
 * event has arrived. Once the box has left the building (a `trackingStatus`
 * exists) the correct recovery is a reship, not a void.
 */
export function canVoidShipmentLeg(leg: {
  status: ShipmentLegStatus;
  trackingStatus: ShipmentLegTrackingStatus | null;
}): boolean {
  return leg.status === "label_purchased" && leg.trackingStatus === null;
}

/**
 * Which leg of the journey a merged tracking event belongs to. `first_mile` /
 * `last_mile` come from a `shipment_leg`; `onward` is the inner parcel's own
 * carrier tracking (the leg from the consolidator to the customer).
 */
export type TrackingTimelineMile = "first_mile" | "last_mile" | "onward";

/** A tracking event tagged with which leg of the journey it came from. */
export interface MergedTrackingEvent extends StoredTrackingEvent {
  mile: TrackingTimelineMile;
}

function legKindToMile(kind: ShipmentLegKind): TrackingTimelineMile {
  return kind === "last_mile" ? "last_mile" : "first_mile";
}

/**
 * Merges a parcel's own carrier tracking with the tracking of every
 * consolidation leg that carried it, into one timeline sorted by `datetime`.
 *
 * Sorting (rather than a blind prepend) keeps a late scan-in or a corrected
 * event correctly interleaved. Each event is tagged with its `mile` so a
 * consumer can phrase first-/last-mile events sensibly for a customer.
 */
export function mergeTrackingTimeline(
  legTimelines: { kind: ShipmentLegKind; events: StoredTrackingEvent[] }[],
  ownEvents: StoredTrackingEvent[],
): MergedTrackingEvent[] {
  const merged: MergedTrackingEvent[] = [
    ...legTimelines.flatMap((leg) =>
      leg.events.map((event) => ({ ...event, mile: legKindToMile(leg.kind) })),
    ),
    ...ownEvents.map((event) => ({ ...event, mile: "onward" as const })),
  ];
  return merged.sort((a, b) => a.datetime.getTime() - b.datetime.getTime());
}

/** Re-export id types for repo/handler convenience. */

export { type ShipmentLegId, type ShipmentLegParcelId } from "../ids";
